import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, UserPlus, GitBranch, Calendar as CalendarIcon, MessagesSquare, StickyNote, Briefcase, Handshake, Building2, ArrowRight, Phone, Star } from "lucide-react";

const KIND_META = {
    candidate_new:          { icon: UserPlus, tint: "emerald" },
    stage_change:           { icon: GitBranch, tint: "amber" },
    rating_given:           { icon: Star, tint: "amber" },
    interview_scheduled:    { icon: CalendarIcon, tint: "violet" },
    interview_rescheduled:  { icon: CalendarIcon, tint: "violet" },
    interview_cancelled:    { icon: CalendarIcon, tint: "rose" },
    message_sent:           { icon: MessagesSquare, tint: "sky" },
    note_added:             { icon: StickyNote, tint: "yellow" },
    job_created:            { icon: Briefcase, tint: "blue" },
    job_updated:            { icon: Briefcase, tint: "blue" },
    job_cloned:             { icon: Briefcase, tint: "blue" },
    offer_sent:             { icon: Handshake, tint: "rose" },
    offer_accepted:         { icon: Handshake, tint: "emerald" },
    offer_declined:         { icon: Handshake, tint: "rose" },
    client_created:         { icon: Building2, tint: "slate" },
    followup_logged:        { icon: Phone, tint: "slate" },
};

const TINT_CLASS = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber:   "border-amber-200 bg-amber-50 text-amber-700",
    violet:  "border-violet-200 bg-violet-50 text-violet-700",
    sky:     "border-sky-200 bg-sky-50 text-sky-700",
    yellow:  "border-yellow-200 bg-yellow-50 text-yellow-800",
    blue:    "border-blue-200 bg-blue-50 text-blue-700",
    rose:    "border-rose-200 bg-rose-50 text-rose-700",
    slate:   "border-slate-200 bg-slate-100 text-slate-700",
};

function timeAgo(iso) {
    if (!iso) return "";
    const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60); if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24); if (d < 7) return `${d}d`;
    return new Date(iso).toLocaleDateString();
}

/**
 * Compact Latest Updates widget — 6-row peek of the main /feed page. Used
 * on the Dashboard so recruiters see fresh activity the moment they land.
 */
export default function FeedWidget() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        const load = async () => {
            try {
                const { data } = await api.get("/feed", { params: { limit: 6 } });
                if (alive) setItems(data?.items || []);
            } catch { /* silent */ }
            finally { if (alive) setLoading(false); }
        };
        load();
        const t = setInterval(load, 60000);
        return () => { alive = false; clearInterval(t); };
    }, []);

    return (
        <section className="border border-border bg-white" data-testid="dashboard-feed-widget">
            <header className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Live</div>
                    <div className="font-display font-bold text-base">Latest updates</div>
                </div>
                <Link
                    to="/feed"
                    className="text-[11px] font-mono uppercase tracking-widest text-ikb hover:underline flex items-center gap-1"
                    data-testid="feed-widget-see-all"
                >
                    See all <ArrowRight className="w-3 h-3" />
                </Link>
            </header>
            {loading ? (
                <div className="p-4 text-xs font-mono text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                    Nothing yet — activity will show up here as candidates apply and stages move.
                </div>
            ) : (
                <ul className="divide-y divide-border" data-testid="feed-widget-list">
                    {items.map((e) => (
                        <FeedWidgetRow key={e.id} evt={e} />
                    ))}
                </ul>
            )}
        </section>
    );
}

function FeedWidgetRow({ evt }) {
    const meta = KIND_META[evt.kind] || { icon: Sparkles, tint: "slate" };
    const Icon = meta.icon;
    const cand = evt.candidate;
    const job = evt.job;
    const primaryHref = cand?.id ? `/candidates/${cand.id}`
                     : job?.id  ? `/jobs/${job.id}`
                     : "/feed";
    return (
        <li>
            <Link
                to={primaryHref}
                className="flex items-start gap-3 p-3 hover:bg-secondary/50"
                data-testid={`feed-widget-row-${evt.id}`}
            >
                <div className={`w-7 h-7 shrink-0 border flex items-center justify-center ${TINT_CLASS[meta.tint]}`}>
                    <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">
                        <span className="font-medium">{cand?.name || job?.title || evt.meta?.name || evt.kind}</span>
                        <span className="text-muted-foreground"> · {shortSubtitle(evt)}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        {typeof evt.match_score === "number" && (
                            <span className={`text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 border ${
                                evt.match_score >= 80 ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                : evt.match_score >= 50 ? "border-amber-200 bg-amber-50 text-amber-700"
                                : "border-rose-200 bg-rose-50 text-rose-700"
                            }`}>
                                AI {evt.match_score}
                            </span>
                        )}
                        <span className="text-[10px] font-mono text-muted-foreground">{timeAgo(evt.created_at)}</span>
                    </div>
                    {evt.kind === "candidate_new" && evt.ai_summary && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-1 leading-snug">{evt.ai_summary}</p>
                    )}
                </div>
            </Link>
        </li>
    );
}

function shortSubtitle(evt) {
    if (evt.kind === "candidate_new") return `applied to ${evt.job?.title || "a role"}`;
    if (evt.kind === "stage_change") return `moved to ${evt.meta?.stage || evt.meta?.new_stage || "next stage"}`;
    if (evt.kind === "message_sent") return "new message";
    if (evt.kind === "note_added") return "note added";
    if (evt.kind.startsWith("interview")) return evt.kind.replace("interview_", "");
    if (evt.kind.startsWith("job")) return evt.kind.replace("job_", "");
    if (evt.kind.startsWith("offer")) return evt.kind.replace("offer_", "");
    if (evt.kind === "client_created") return "new client";
    if (evt.kind === "followup_logged") return "follow-up";
    return evt.kind;
}
